import { getEnv } from '@/lib/env'
import { useStripePayment } from '@/lib/stripe'
import { useState, useCallback, useEffect, useRef } from 'react'
import { useGetMeetingLinkQuery, useGetPaymentStatusQuery, useUpdateOrderMutation } from '@/core/graphql/hooks'
import { AvailabilityMeetingPlace, CreateOrderInput, OrderStatus } from '@/core/graphql/types'
import { useBooking } from './bookingContext'
import { useGetMeetingLink } from '@/shared/api/meeting-link'
import { ConsultationMode } from './bookingTypes'

interface PaymentConfig {
  requiresPayment?: boolean
  basePrice?: number
  paymentReceiver?: string
  priceModifiers?: Array<{
    meetingPlace: AvailabilityMeetingPlace
    modifier: number
  }>
}

export function usePayment(meetingLinkSlug: string) {
  const [error, setError] = useState<Error | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [updateOrder] = useUpdateOrderMutation()
  const { state, dispatch } = useBooking()

  const orderId = state.orderId

  // Add a ref to track the previous orderId to prevent unnecessary re-renders
  const previousOrderIdRef = useRef<string | null>(null)

  // Add a ref to track if an update is in progress
  const isUpdatingRef = useRef(false)

  // Get meeting link data
  const { meetingLink, isLoadingMeetingLink } = useGetMeetingLink(meetingLinkSlug)
  // Get the meeting link ID from the query result
  const meetingLinkId = meetingLink?._id

  const { data: paymentStatusData, loading: isPaymentStatusLoading } = useGetPaymentStatusQuery({
    variables: { payload: { orderId: orderId || '' } },
    skip: !orderId,
  })

  // Generate dynamic success and cancel URLs
  const getPaymentUrls = () => {
    // Get the base URL (works in both client and server)
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : getEnv('NEXT_PUBLIC_APP_URL', 'http://localhost:4000')

    return {
      // Updated paths for success and cancel pages within the booking flow
      successUrl: `${baseUrl}/meeting-links/${meetingLinkSlug}/booking/payment/success?orderId=${orderId}`,
      cancelUrl: `${baseUrl}/meeting-links/${meetingLinkSlug}/booking/payment/cancel?orderId=${orderId}`,
    }
  }

  // Initialize Stripe payment hook
  const {
    initializePayment,
    isRedirecting,
    isCheckingStatus,
    error: stripeError,
    paymentStatus,
  } = useStripePayment({
    orderId: orderId || '',
    ...getPaymentUrls(), // Pass dynamic success and cancel URLs
    onSuccess: async () => {
      try {
        // Update order status to Pending after successful payment
        if (orderId && !isUpdatingRef.current && meetingLinkId) {
          // Set flag to prevent multiple simultaneous updates
          isUpdatingRef.current = true

          console.log('🔍 [ORDER TRACKING] usePayment - Payment successful, updating order:', orderId)
          console.log('🔍 [ORDER TRACKING] usePayment - Using meeting link ID:', meetingLinkId)

          // Update context with payment completed
          dispatch({ type: 'SET_PAYMENT_COMPLETED', payload: true })

          // Prepare update payload
          const updatePayload: CreateOrderInput = {
            status: OrderStatus.Pending,
            meetingLink: meetingLinkId, // Use the MongoDB ID, not the slug
            speciality: state.selectedSpeciality || '',
            startDate: state.startDate || new Date().toISOString(),
            meetingPlace: {
              placeId: state.meetingPlace || '',
              description: state.meetingPlaceDescription || '',
            },
            patientInfo: {
              firstName: state.patientInfo?.firstName || '',
              lastName: state.patientInfo?.lastName || '',
              email: state.patientInfo?.email || '',
              phone: state.patientInfo?.phone,
            },
          }

          try {
            const result = await updateOrder({
              variables: {
                orderId,
                input: updatePayload,
              },
            })
            console.log('🔍 [ORDER TRACKING] usePayment - Order updated successfully:', result)
          } catch (updateError) {
            console.error('🔍 [ORDER TRACKING] usePayment - Error updating order:', updateError)
            setError(updateError instanceof Error ? updateError : new Error('Failed to update order after payment'))

            // Set error in context
            dispatch({
              type: 'SET_ERROR',
              payload: { field: 'payment', message: 'Failed to update order after payment' },
            })
          } finally {
            // Reset the updating flag
            isUpdatingRef.current = false
          }
        } else {
          if (!meetingLinkId) {
            console.error('🔍 [ORDER TRACKING] usePayment - Cannot update order: Missing meeting link ID')
            setError(new Error('Missing meeting link ID'))
          }
        }
      } catch (err) {
        console.error('Error updating order after payment:', err)
        setError(err instanceof Error ? err : new Error('Failed to update order after payment'))

        // Set error in context
        dispatch({
          type: 'SET_ERROR',
          payload: { field: 'payment', message: 'Failed to update order after payment' },
        })

        // Reset the updating flag
        isUpdatingRef.current = false
      }
    },
    onError: (err) => {
      setError(err)

      // Set error in context
      dispatch({
        type: 'SET_ERROR',
        payload: { field: 'payment', message: err.message || 'Payment failed' },
      })
    },
  })

  // Calculate final price based on payment config and modifiers
  const calculateFinalPrice = useCallback(
    (basePrice: number, paymentConfig?: PaymentConfig) => {
      if (!paymentConfig) return basePrice

      const { priceModifiers } = paymentConfig
      if (!priceModifiers?.length) return basePrice

      const modifier = priceModifiers.find((mod) => mod.meetingPlace === state.meetingPlace)

      return modifier ? basePrice * modifier.modifier : basePrice
    },
    [state.meetingPlace]
  )

  // Get payment details for display and processing
  const getPaymentDetails = useCallback(() => {
    if (!state.requiresPayment) return null

    // For immediate consultations, use the immediateConsultation config
    if (state.consultationMode === ConsultationMode.Immediate) {
      const immediateConfig = meetingLink?.immediateConsultationConfig
      if (!immediateConfig?.price || !immediateConfig.paymentReceiver) return null

      return {
        basePrice: immediateConfig.price,
        finalPrice: immediateConfig.price,
        paymentReceiver: immediateConfig.paymentReceiver,
      }
    }

    const availability = meetingLink?.availabilities?.find((a) => a.meetingPlace === state.meetingPlace)

    if (!availability?.paymentConfig) return null

    const paymentConfig = availability.paymentConfig
    const basePrice = paymentConfig.basePrice || 0

    // Create a compatible PaymentConfig object for calculateFinalPrice
    const compatibleConfig: PaymentConfig = {
      requiresPayment: paymentConfig.requiresPayment || false,
      basePrice: paymentConfig.basePrice || 0,
      paymentReceiver: paymentConfig.paymentReceiver?.stripe || '',
      priceModifiers: [],
    }

    const finalPrice = calculateFinalPrice(basePrice, compatibleConfig)

    return {
      basePrice,
      finalPrice,
      paymentReceiver: paymentConfig.paymentReceiver,
    }
  }, [meetingLink, calculateFinalPrice, state.requiresPayment, state.meetingPlace, state.consultationMode])

  // Add a specific function to validate the order ID
  const validateOrderId = useCallback(() => {
    if (!orderId) {
      setError(new Error('No order ID found. Please complete the previous steps'))
      return false
    }

    return true
  }, [orderId])

  // Handle payment initiation with improved validation
  const handlePayment = useCallback(async () => {
    if (!validateOrderId()) return

    try {
      setIsProcessing(true)
      setError(null)
      await initializePayment()
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Payment failed'))

      // Set error in context
      dispatch({
        type: 'SET_ERROR',
        payload: { field: 'payment', message: err instanceof Error ? err.message : 'Payment failed' },
      })
    } finally {
      setIsProcessing(false)
    }
  }, [initializePayment, getPaymentDetails, dispatch, validateOrderId])

  // Set error from Stripe if any
  useEffect(() => {
    if (stripeError) {
      setError(stripeError)

      // Set error in context
      dispatch({
        type: 'SET_ERROR',
        payload: { field: 'payment', message: stripeError.message || 'Payment failed' },
      })
    }
  }, [stripeError, dispatch])

  // Update the previous orderId ref when it changes
  useEffect(() => {
    previousOrderIdRef.current = orderId
  }, [orderId])

  return {
    isLoading: isLoadingMeetingLink || isRedirecting || isCheckingStatus || isPaymentStatusLoading,
    isProcessing,
    error,
    handlePayment,
    paymentStatus: paymentStatusData?.getPaymentStatus,
    paymentDetails: getPaymentDetails(),
    meetingLinkId,
  }
}
